import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ExternalLink, ArrowRight } from 'lucide-react';

// Import logos from assets folder. The `?as=picture` directive emits
// AVIF/WebP/JPEG at logo-appropriate widths — the source files were
// 1920×~1100 full-colour photos, vastly oversized for an 80px tall logo
// render. With the imagetools pipeline the 240w AVIF variant is under 5 KB.
import { ResponsiveImage, type Picture } from '@/components/common/ResponsiveImage';
import elevationWindowsLogo from '@/assets/elevation-windows-logo.jpg?w=120;240;360&format=avif;webp;jpg&as=picture';
import elevationBuildersLogo from '@/assets/elevation-builders-logo.jpg?w=120;240;360&format=avif;webp;jpg&as=picture';
import elevationElectricalLogo from '@/assets/elevation-electrical-logo.jpg?w=120;240;360&format=avif;webp;jpg&as=picture';

// Configuration - This is Elevation Electrical's website
const CURRENT_SITE = 'electrical';

type Company = {
  id: string;
  name: string;
  tagline: string;
  logo: Picture;
  borderColor: string;
  titleColor: string;
  buttonBg: string;
  services: string[];
  website: string;
  internalLink: string;
};

const ELEVATION_COMPANIES: Company[] = [
  {
    id: 'windows',
    name: 'Elevation Windows & Doors',
    tagline: 'Premium window and door solutions',
    logo: elevationWindowsLogo,
    borderColor: 'border-[#0062A8]',
    titleColor: 'text-[#0062A8]',
    buttonBg: 'bg-[#0062A8] hover:bg-[#0062A8]/90',
    services: [
      'Premium PGT and MI products',
      'Expert installation services',
      'Energy-efficient solutions',
      'Hurricane protection'
    ],
    website: 'https://elevationwindowsfl.com',
    internalLink: '/services',
  },
  {
    id: 'builders',
    name: 'Elevation Builders Group',
    tagline: 'Quality construction services',
    logo: elevationBuildersLogo,
    borderColor: 'border-[#003454]',
    titleColor: 'text-[#003454]',
    buttonBg: 'bg-[#003454] hover:bg-[#003454]/90',
    services: [
      'Licensed building contractors',
      'Home renovations',
      'Custom home building',
      'Commercial construction'
    ],
    website: 'https://elevationbuildersgroup.com',
    internalLink: '/services',
  },
  {
    id: 'electrical',
    name: 'Elevation Electrical',
    tagline: 'Professional electrical services',
    logo: elevationElectricalLogo,
    borderColor: 'border-orange',
    titleColor: 'text-orange',
    buttonBg: 'bg-orange hover:bg-orange/90',
    services: [
      'Licensed electrical contractors',
      'Residential & commercial',
      'Panel upgrades',
      'Backup Power Generator Solutions'
    ],
    website: 'https://www.elevationelectricalfl.com',
    internalLink: '/services',
  }
];

const ElevationFamily: React.FC = () => {
  return (
    <section className="py-16 bg-gradient-to-br from-navy via-navy to-navy/95">
      <div className="container-custom">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Part of the Elevation Family
          </h2>
          <p className="text-white/80 max-w-3xl mx-auto text-lg">
            We're proud to be affiliated with Elevation Windows & Doors and Elevation Builders Group, 
            offering comprehensive solutions for all your home improvement needs.
          </p>
        </div>

        {/* Company Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {ELEVATION_COMPANIES.map((company) => {
            const isCurrentSite = company.id === CURRENT_SITE;

            return (
              <div 
                key={company.id}
                className={`bg-white rounded-xl overflow-hidden shadow-lg border-b-4 ${company.borderColor} transition-transform hover:-translate-y-1`}
              >
                {/* Logo Container */}
                <div className="p-6 flex items-center justify-center bg-gray-50 border-b h-[112px]">
                  <ResponsiveImage
                    picture={company.logo}
                    sizes="240px"
                    alt={`${company.name} logo`}
                    className="h-20 w-auto object-contain"
                  />
                </div>

                {/* Card Body */}
                <div className="p-6">
                  <h3 className={`text-xl font-bold mb-1 ${company.titleColor}`}>
                    {company.name}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {company.tagline}
                  </p>

                  {/* Services List */}
                  <ul className="space-y-2 mb-6">
                    {company.services.map((service, idx) => (
                      <li key={idx} className="text-sm text-navy/80 flex items-start">
                        <span className="text-navy/40 mr-2">•</span>
                        {service}
                      </li>
                    ))}
                  </ul>

                  {/* Button - Internal link for current site, external for others */}
                  {isCurrentSite ? (
                    <Link to={company.internalLink} className="block">
                      <Button className={`w-full ${company.buttonBg} text-white`}>
                        Our Services
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  ) : (
                    <a 
                      href={company.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <Button className={`w-full ${company.buttonBg} text-white`}>
                        Visit Website
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Synergy Advantage Section */}
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            The Synergy Advantage
          </h3>
          <p className="text-white/80 max-w-3xl mx-auto mb-6">
            When you work with any Elevation company, you gain access to our entire network of skilled professionals
            across construction, electrical, and window/door installation—all working together 
            under one unified vision of quality and excellence.
          </p>
          <Link to="/affiliations">
            <Button className="bg-white text-navy hover:bg-white/90 font-semibold">
              Learn More About Our Companies
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ElevationFamily;
